import {FormLayout, FormActions, TextField, SelectField} from '@hconnect/common/components'
import {isCustomRepetition} from '@hconnect/common/types'
import {isSameDay} from '@hconnect/uikit/src/lib2/components/momentDatePicker/dateRangePickerHelpers'
import {yupResolver} from '@hookform/resolvers/yup'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {AttachmentsField} from '@widgets/common-attachments'
import {isUndefined} from 'lodash'
import {Moment} from 'moment-timezone'
import {useEffect, useMemo, useRef} from 'react'
import {FormProvider, useForm, useFormState, SubmitHandler} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useTaskPlantsOptions, useWatchFormValue} from '../../hooks'
import {
  PROCESS_WORK_ORDER_STATUSES,
  ProcessWorkOrderAttachment,
  ProcessWorkOrderFormValues,
  TaskSource,
  TaskStatus
} from '../../types'
import {convertAttachmentsToFiles} from '../../utils'
import {getProcessWorkOrderValidationSchema} from '../../validators'

import {EquipmentSearchField} from './EquipmentSearchField'
import {MainEquipmentSearchField} from './MainEquipmentSearchField'
import {ProcessStageField} from './ProcessStageField'
import {ProcessWorkOrdersUserSearchFormField} from './ProcessWorkOrdersUserSearchFormField'
import {RichTextFormField, MomentDateRangePickerFormField} from './shared'
import {TaskChecklistsFormField} from './TaskChecklistsFormField'
import {TaskRepetition} from './TaskRepetition'
import {TaskRepetitionDueDatesPreview} from './TaskRepetitionDueDatesPreview'
import {TaskStatusField} from './TaskStatusField'
import {TaskTypeField} from './TaskTypeField'

type Props = {
  onSubmit: SubmitHandler<ProcessWorkOrderFormValues>
  onCancel: () => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  initialValues?: Partial<ProcessWorkOrderFormValues>
  disableRepetition?: boolean
  disableRecurringToggle?: boolean
  disableChecklistsSelect: boolean
  externalTaskId?: string
  initialAttachments?: ProcessWorkOrderAttachment[]
  plantTimezone?: string
  onStatusChange?: (newValue: TaskStatus | undefined, resetStatus: () => void) => void
}

const defaultValues: Partial<ProcessWorkOrderFormValues> = {
  title: '',
  description: '',
  plantId: undefined,
  equipment: undefined,
  mainEquipment: undefined,
  processStage: undefined,
  status: TaskStatus.Pending,
  doNotStartBefore: undefined,
  dueDate: undefined,
  attachments: [],
  assignees: [],
  isRecurring: false,
  checklists: []
}

const getProcessWorkOrderInitialValues = (initialValues: Partial<ProcessWorkOrderFormValues>) => {
  return {
    ...defaultValues,
    ...initialValues,
    attachments: [] // Note: This is initialized in useEffect since Files are fetched asynchronously
  } as ProcessWorkOrderFormValues
}

export const ProcessWorkOrderForm = ({
  initialValues,
  disableChecklistsSelect,
  externalTaskId,
  initialAttachments,
  plantTimezone,
  ...props
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const validationSchema = useMemo(
    () =>
      getProcessWorkOrderValidationSchema({
        t,
        disableRepetition: props.disableRepetition,
        originalDueDate: initialValues?.dueDate,
        plantTimezone
      }),
    [t, props.disableRepetition, initialValues?.dueDate, plantTimezone]
  )

  const methods = useForm<ProcessWorkOrderFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues ? getProcessWorkOrderInitialValues(initialValues) : defaultValues
  })

  const {isValid, isSubmitting, isDirty} = useFormState({
    control: methods.control
  })

  const didInitAttachments = useRef(false)

  useEffect(() => {
    if (didInitAttachments.current || !initialValues?.attachments?.length) return
    didInitAttachments.current = true

    const loadAttachmentsAndValidate = async () => {
      const files = await convertAttachmentsToFiles(initialAttachments ?? [])
      methods.setValue('attachments', files)
      await methods.trigger()
    }

    void loadAttachmentsAndValidate()
  }, [initialAttachments, initialValues?.attachments, methods])

  useEffect(() => {
    // TODO: investigate why initialValues?.plantId is not being set and triggering the form validation
    const setPlantIdAndTrigger = async () => {
      if (initialValues?.plantId) {
        methods.setValue('plantId', initialValues.plantId)
        isDirty && (await methods.trigger())
      }
    }

    void setPlantIdAndTrigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.plantId, isDirty, methods])

  const plantId = methods.watch('plantId')

  const showChecklists = useCommonFeatureFlag(FeatureFlagKey.Checklists, {plantId})

  const dueDate = useWatchFormValue(methods, 'dueDate')
  const doNotStartBefore = useWatchFormValue(methods, 'doNotStartBefore')
  const isRecurring = useWatchFormValue(methods, 'isRecurring')
  const repetitionInfo = useWatchFormValue(methods, 'repetitionInfo')
  const plantOptions = useTaskPlantsOptions(TaskSource.PROCESS_WORK_ORDER)
  const mainEquipment = useWatchFormValue(methods, 'mainEquipment')
  // Display equipment fields and recurring checkbox only if plantId is selected or initialValues are provided in edit mode
  const hasPlantSpecificFields = useMemo(() => plantId || !!initialValues, [plantId, initialValues])

  // Memoize due date comparison to avoid recalculating on every render
  const isDueDateRange = useMemo(
    () => !!(doNotStartBefore && dueDate && !isSameDay([dueDate, doNotStartBefore])),
    [dueDate, doNotStartBefore]
  )
  // Check if TaskRepetition should be disabled
  const isTaskRepetitionDisabled = props.disableRepetition || isDueDateRange

  useEffect(() => {
    if (isDirty && dueDate) void methods.trigger()
  }, [methods, dueDate, isDirty])

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(props.onSubmit)}>
        <FormLayout>
          <TaskTypeField value={TaskSource.PROCESS_WORK_ORDER} onChange={props.onTaskTypeChange} />
          <TextField name="title" label={t('task.label.title')} control={methods.control} />
          <RichTextFormField
            name="description"
            label={t('task.label.description')}
            pressAndHoldRecordLabel={t('task.label.pressAndHoldRecord')}
            control={methods.control}
          />
          <SelectField
            disabled={!!initialValues}
            name="plantId"
            label={t('task.label.plant')}
            control={methods.control}
            options={plantOptions}
            onChange={() => {
              methods.resetField('equipment')
              methods.resetField('mainEquipment')
              methods.resetField('processStage')
              methods.resetField('assignees')
              methods.resetField('dueDate')
              methods.resetField('doNotStartBefore')
              methods.resetField('isRecurring')
              methods.resetField('repetitionInfo')
            }}
          />

          {hasPlantSpecificFields && (
            <>
              <EquipmentSearchField
                plantId={plantId}
                name="equipment"
                label={t('task.label.functionalLocation')}
                control={methods.control}
                onChange={(equipment) => {
                  const mainEquipmentData = isUndefined(equipment)
                    ? undefined
                    : (methods.getValues('mainEquipment') ?? equipment?.mainEquipment)
                  methods.setValue('mainEquipment', mainEquipmentData)
                  methods.setValue('processStage', equipment?.processStage?.processStep)
                }}
                mainEquipmentId={mainEquipment?.id}
                optional
              />
              <MainEquipmentSearchField
                plantId={plantId}
                name="mainEquipment"
                label={t('task.label.mainEquipment')}
                control={methods.control}
                optional
              />
              <ProcessStageField
                plantId={plantId}
                name="processStage"
                label={t('task.label.processStage')}
                control={methods.control}
                optional
              />
              <ProcessWorkOrdersUserSearchFormField
                plantId={plantId}
                name="assignees"
                label={t('task.label.assignee')}
                pleaseStartTypingLabel={t('task.label.pleaseStartTyping')}
                control={methods.control}
                optional
              />
              <TaskRepetition<ProcessWorkOrderFormValues>
                dataTestId="recurring-checkbox"
                name="isRecurring"
                repetitionInfoName="repetitionInfo"
                control={methods.control}
                label={t('task.label.repetition')}
                isRecurring={isRecurring}
                isTooltipDisabled={!isDueDateRange}
                onDueDateChange={(dueDate: Moment) => {
                  methods.resetField('doNotStartBefore')
                  methods.setValue('dueDate', dueDate, {shouldValidate: true})
                }}
                disabled={isTaskRepetitionDisabled}
                disableRecurringToggle={props.disableRecurringToggle}
                plantId={plantId}
              />
              {!props.disableRepetition && (
                <TaskRepetitionDueDatesPreview
                  settings={repetitionInfo}
                  plantId={plantId}
                  defaultStartDate={dueDate}
                  isRecurring={isRecurring}
                />
              )}

              {!(repetitionInfo && isCustomRepetition(repetitionInfo)) && (
                <MomentDateRangePickerFormField
                  dataTestId="pwo-due-date-range-picker"
                  startName="doNotStartBefore"
                  endName="dueDate"
                  disableDateSwitch={props.disableRepetition && isRecurring}
                  label={t('task.label.dueDate')}
                  control={methods.control}
                  plantId={plantId}
                  onChange={(startDate, endDate) => {
                    // If both dates are set and different (date range)
                    // disable repetition fields and reset repetition values
                    if (startDate && endDate && !isSameDay([startDate, endDate])) {
                      if (isRecurring || repetitionInfo) {
                        methods.setValue('isRecurring', false)
                        methods.setValue('repetitionInfo', undefined as never)
                      }
                    }
                  }}
                />
              )}

              {showChecklists && (
                <TaskChecklistsFormField
                  label={t('task.label.checklists')}
                  name="checklists"
                  control={methods.control}
                  plantId={plantId}
                  disabled={disableChecklistsSelect}
                  externalTaskId={externalTaskId}
                  isEditMode={!!initialValues}
                />
              )}
            </>
          )}

          <TaskStatusField
            name="status"
            label={t('task.label.status')}
            control={methods.control}
            allowedStatuses={PROCESS_WORK_ORDER_STATUSES}
            onChange={(newValue, previousValue) => {
              props.onStatusChange?.(newValue, () => {
                methods.setValue('status', previousValue as ProcessWorkOrderFormValues['status'])
              })
            }}
          />

          <AttachmentsField name="attachments" control={methods.control} />

          <FormActions onCancel={props.onCancel} isSubmitting={isSubmitting} isValid={isValid} />
        </FormLayout>
      </form>
    </FormProvider>
  )
}
